import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Account } from '@/account/index';
import { useAppConfigContext, useAppConfigRedirect } from '@/context/AppConfig';
import {
  Button,
  FormControlLabel,
  Stack,
  ErrorMessage,
  Input,
  Text,
  styled,
  Checkbox,
} from '@/theme/index';
import { schemaResetPasswordForm } from '@/utils/schemaForms/schemaForms';
import { useSession } from './Session';
import { ValidationErrorMessage } from './components';

type ResponseData = Awaited<ReturnType<typeof Account.resetPassword>>;

type RequestParams = Parameters<typeof Account.resetPassword>[0];

const StyledFormControlLabel = styled(FormControlLabel)(
  ({ theme }) => `
  align-items: flex-start;
  margin: ${theme.spacing(0)};
  gap: ${theme.spacing(1)};
  .MuiTypography-root {
    font-weight: bold;
    font-size: 16px;
    line-height: 1.2;
  }
  .MuiInputBase-input {
    padding: ${theme.spacing(1.25)};
  }
`
);

interface ResetPasswordFormProps {
  /** The token that was sent to the user's email, it will get passed in via the URL query string in the route */
  token: string;
}

/** This component allows users to reset their password. It's going to ask for a new password and confirmation. */
export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  token,
}) => {
  const { logError } = useAppConfigContext();
  const { setSession } = useSession();
  const { redirect } = useAppConfigRedirect();
  const { handleSubmit, register, formState, getFieldState } = useForm({
    resolver: yupResolver(schemaResetPasswordForm),
    defaultValues: {
      password: '',
      passwordConfirmation: '',
      invalidateSessions: false,
      token,
    },
  });
  const resetPasswordMutation = useMutation<
    ResponseData,
    Error & { error: string },
    RequestParams
  >({
    mutationFn: Account.resetPassword,

    onSuccess: () => {
      setSession({
        isAuthenticated: true,
      });
      redirect('/dashboard');
    },
    onError: (error) => {
      logError('ResetPasswordForm', error);
    },
  });

  const buttonText = resetPasswordMutation.isLoading
    ? 'Processing...'
    : 'Set Password and Login';

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      paddingY={11.375}
      paddingX={4.375}
    >
      <Stack
        onSubmit={handleSubmit((data) => {
          resetPasswordMutation.mutate(data);
        })}
        component="form"
        maxWidth={700}
        spacing={2.5}
      >
        {resetPasswordMutation.isError && (
          <ErrorMessage message={resetPasswordMutation.error.error} />
        )}
        <Stack spacing={1} marginBottom={3}>
          <Text variant="h1" lineHeight={1.2} sx={{ margin: 0 }}>
            Reset Password
          </Text>
          <Text variant="body1">
            Type in your password below and click the button below to update
            your account. You will be signed in automatically.
          </Text>
        </Stack>
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            <StyledFormControlLabel
              control={
                <Input
                  {...register('password')}
                  fullWidth
                  type="password"
                  id="password"
                  name="password"
                  error={getFieldState('password').invalid}
                  data-testid="password"
                />
              }
              label="New password"
              labelPlacement="top"
            />
            {formState.errors.password?.message && (
              <ValidationErrorMessage>
                {formState.errors.password?.message}
              </ValidationErrorMessage>
            )}
          </Stack>
          <Stack spacing={0.5}>
            <StyledFormControlLabel
              control={
                <Input
                  {...register('passwordConfirmation')}
                  fullWidth
                  type="password"
                  id="password-confirmation"
                  name="passwordConfirmation"
                  error={getFieldState('passwordConfirmation').invalid}
                  data-testid="password-confirmation"
                />
              }
              label="Confirm your new password"
              labelPlacement="top"
            />
            {formState.errors.passwordConfirmation?.message && (
              <ValidationErrorMessage>
                {formState.errors.passwordConfirmation?.message}
              </ValidationErrorMessage>
            )}
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                {...register('invalidateSessions')}
                sx={(theme) => ({
                  padding: 0,
                  color: theme.palette.primary.main,
                })}
              />
            }
            label="Logout all other active sessions"
            // override margin given by FormControlLabel to allow
            // checkbox to be aligned with other form fields
            sx={{ marginLeft: '-3.42px !important', gap: 0.5 }}
          />
          <Button type="submit" variant="contained" fullWidth color="success">
            {buttonText}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
